import React, { useContext, useState } from 'react'

import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'

import {
  ArcadeButton,
  ArcadeStatusBanner,
  LoadingIcon,
  PaddingInset,
  spacing,
  Stack,
  Text,
} from '@cash-design-system/react'
import { ClientScenario } from '@cash-web/protos/squareup/franklin/api/client_scenario.pb'
import { HelpItem, HelpItemAction } from '@cash-web/protos/squareup/franklin/api/help_item.pb'
import { SkipBlockerResponseStatus } from '@cash-web/protos/squareup/franklin/app/blocker.pb'
import { ResolveMergeResponseStatus } from '@cash-web/protos/squareup/franklin/app/merge.pb'
import { GetScenarioPlanResponseStatus } from '@cash-web/protos/squareup/franklin/app/scenario.pb'
import { SelectOptionResponseStatus } from '@cash-web/protos/squareup/franklin/app/select_option.pb'
import { ResponseContext } from '@cash-web/protos/squareup/franklin/ResponseContextProto.pb'
import { useLogout } from '@cash-web/shared-auth-feature-main'
import { useBlockerResponseHandler } from '@cash-web/shared-blocker-data-access-context'
import { useMutationGetScenarioPlan } from '@cash-web/shared-data-access-api/react-query/useMutationGetScenarioPlan'
import { useMutationResolveMerge } from '@cash-web/shared-data-access-api/react-query/useMutationResolveMerge'
import { useMutationSelectOption } from '@cash-web/shared-data-access-api/react-query/useMutationSelectOption'
// eslint-disable-next-line @nx/workspace/no-new-franklin-get-profile -- pre-existing usage; migrate per #cash-profile-migration
import { useQueryGetProfile } from '@cash-web/shared-data-access-api/react-query/useQueryGetProfile'
import { ErrorContext } from '@cash-web/shared-data-access-error'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { FlowContext } from '@cash-web/shared-flow-data-access-context'
import { FlowProvider, useFlow } from '@cash-web/shared-flow-feature-main'
import { useJourneyContext } from '@cash-web/shared-journey-data-access-context'
import { WebInteractClick } from '@squareup/customer-data-events-web'

import { catchPromise } from '../../util/catchPromise'
import { getSelectOptionErrors, getStatusError } from '../../util/statusErrors'
import { FlowRenderer } from '../FlowRenderer'
import { getSupportedHelpItems } from './getSupportedHelpItems'
export interface CustomActionResponse {
  success: boolean
  message?: string
}

export interface HelpModalItem extends HelpItem {
  // This is meant to support legacy blockers with hard-coded help action items that are not defined
  // in the proto
  customAction?: () => Promise<CustomActionResponse>
}
interface Props {
  onClose: () => void
  onComplete?: () => void
  helpItems: HelpModalItem[]
  title?: string
  subtext?: string
}

const MainHeading = styled(Text)`
  margin-bottom: ${({ theme }) => theme.spacing.m};
  word-break: break-word;
`
const SubHeading = styled(Text)`
  word-break: break-word;
`

export const HelpModalComponent = ({ helpItems, onClose, onComplete, title, subtext }: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { clearError } = useContext(ErrorContext)
  const logCashWebCdfEvent = useLogCashWebCdfEvent()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isCustomActionInProgress, setIsCustomActionInProgress] = useState(false)
  const [actionResponse, setActionResponse] = useState<CustomActionResponse>()
  const { genericError } = useBlockerResponseHandler()
  const logout = useLogout()
  const journey = useJourneyContext()
  const globalFlow = useContext(FlowContext)

  // Create a flow for the help modal
  const helpModalFlow = useFlow({
    onCompleteFlow: () => {
      setIsSubmitting(false)
      onComplete?.()
    },
    onCancelFlow: () => {
      setIsSubmitting(false)
    },
  })

  // Use the mutation options with the flow token from helpModalFlow
  const { mutateAsync: resolveMerge, isLoading: isLoadingResolvingMerge } = useMutationResolveMerge()
  const { mutateAsync: getScenarioPlan, isLoading: isLoadingGettingScenarioPlan } = useMutationGetScenarioPlan()
  const { mutateAsync: selectOption, isLoading: isLoadingSelectingOption } = useMutationSelectOption()

  const isLoading = isLoadingResolvingMerge || isLoadingGettingScenarioPlan || isLoadingSelectingOption

  const handleHelpItemActionSuccess = (responseContext?: ResponseContext) => {
    if (responseContext?.scenario_plan?.blocker_descriptors) {
      globalFlow.updateWithBlockerDescriptors({
        blockerDescriptors: responseContext.scenario_plan.blocker_descriptors,
        queriesToInvalidate: [useQueryGetProfile.rootQueryKey],
      })
    } else {
      globalFlow.complete()
    }
    onClose()
  }

  const handleHelpItemActionFailure = (responseContext?: ResponseContext, failureMessage?: string) => {
    const message =
      failureMessage || responseContext?.failure_message || responseContext?.dialog_message || genericError
    setIsSubmitting(false)
    setActionResponse({
      success: false,
      message,
    })
  }

  const onHelpItemClick = async (helpItem: HelpModalItem) => {
    setIsSubmitting(true)

    if (helpItem.customAction) {
      setIsCustomActionInProgress(true)
      const response = await helpItem.customAction()
      setIsSubmitting(false)
      setIsCustomActionInProgress(false)
      if (response?.success && !response.message) {
        onClose()
      } else {
        setActionResponse(response)
      }
    } else if (helpItem.blocker_action) {
      setIsSubmitting(false)
      onComplete?.()
      onClose()

      helpModalFlow.executeBlockerAction(helpItem.blocker_action, undefined, { replace: false })
    } else {
      switch (helpItem.action) {
        case HelpItemAction.SELECT_OPTION: {
          const response = await catchPromise(
            selectOption({
              action: helpItem.select_option_action,
              options: {
                clientScenario: globalFlow.active?.clientScenario,
                flowToken: globalFlow.active?.flowToken,
              },
            })
          )
          if (!response) break
          if (response.status === SelectOptionResponseStatus.SUCCESS) {
            handleHelpItemActionSuccess(response.response_context)
          } else {
            handleHelpItemActionFailure(
              response.response_context,
              getStatusError(getSelectOptionErrors(t), response.status, genericError)
            )
          }
          break
        }
        case HelpItemAction.GET_SCENARIO_PLAN: {
          const { status, response_context } = await catchPromise(
            getScenarioPlan({
              request_context: {},
              options: { clientScenario: globalFlow.active?.clientScenario, flowToken: globalFlow.active?.flowToken },
            })
          )
          if (status === GetScenarioPlanResponseStatus.SUCCESS) {
            handleHelpItemActionSuccess(response_context)
          } else {
            handleHelpItemActionFailure(response_context)
          }
          break
        }
        case HelpItemAction.SKIP_BLOCKER: {
          const response = await globalFlow.skipBlocker(true)
          if (response.status === SkipBlockerResponseStatus.SUCCESS) {
            handleHelpItemActionSuccess(response.response_context)
          } else {
            handleHelpItemActionFailure(response.response_context)
          }
          break
        }
        case HelpItemAction.RESOLVE_MERGE_FALSE: {
          const { status, response_context } = await catchPromise(
            resolveMerge({
              confirm_merge: false,
              options: {
                clientScenario: globalFlow.active?.clientScenario,
                flowToken: globalFlow.active?.flowToken,
              },
            })
          )
          if (status === ResolveMergeResponseStatus.SUCCESS) {
            handleHelpItemActionSuccess(response_context)
          } else {
            handleHelpItemActionFailure(response_context)
          }
          break
        }
        case HelpItemAction.OPEN_URL: {
          setIsSubmitting(false)
          window.open(helpItem.url)
          break
        }
        case HelpItemAction.SIGN_OUT: {
          setIsSubmitting(false)
          logout({ returnToSameUrl: false })
          break
        }
        case HelpItemAction.START_CLIENT_SCENARIO: {
          if (helpItem.client_scenario != null) {
            logCashWebCdfEvent(
              WebInteractClick({
                componentName: 'HelpModalComponent',
                componentType: `Start Client Scenario [${helpItem.client_scenario}]`,
              })
            )
            const clientScenario = helpItem.client_scenario as ClientScenario

            setIsSubmitting(false)
            onClose?.()
            journey.navigate(`/dl/scenario/${clientScenario}`)

            clearError()
          } else {
            setIsSubmitting(false)
            throw Error(`Help Item - missing client scenario ${JSON.stringify(helpItem)}`)
          }
          break
        }
        default: {
          setIsSubmitting(false)
          throw Error(`Help Item - deprecated action ${JSON.stringify(helpItem)}`)
        }
      }
    }
  }

  let content = null
  if (isSubmitting || isLoading || globalFlow.isLoading || helpModalFlow.isLoading) {
    content = <LoadingIcon />
  } else if (!actionResponse?.success) {
    // No need to show the help items if the action they took was a success
    content = (
      <Stack space={spacing.m}>
        {getSupportedHelpItems(helpItems)?.map(helpItem => {
          return (
            <ArcadeButton
              prominence="standard"
              size="cta"
              key={helpItem.text}
              disabled={isSubmitting}
              onClick={() => onHelpItemClick(helpItem)}
            >
              {helpItem.text}
            </ArcadeButton>
          )
        })}
      </Stack>
    )
  }

  // if we are displaying a blocker we should render only this until we've completed the blocker flow
  if (!isLoading && !isCustomActionInProgress && isSubmitting) {
    return (
      <FlowProvider flow={helpModalFlow}>
        <FlowRenderer />
      </FlowProvider>
    )
  }

  return (
    <Stack space={spacing.m}>
      <PaddingInset>
        {title && (
          <MainHeading typography="numeralSmall" as="h2" color="textStandard">
            {title}
          </MainHeading>
        )}
        {subtext && (
          <SubHeading typography="body" color="textSubtle">
            {subtext}
          </SubHeading>
        )}
      </PaddingInset>
      {content}
      {actionResponse && (
        <ArcadeStatusBanner
          title={actionResponse.message ?? ''}
          variant={actionResponse.success ? 'prominent' : 'error'}
        />
      )}
    </Stack>
  )
}
